import { Datum } from '@ant-design/charts';
import { ClockCircleOutlined, InfoCircleOutlined, ThunderboltFilled } from '@ant-design/icons';
import { Area, AreaConfig } from '@ant-design/plots';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Skeleton from 'antd/lib/skeleton';
import Text from 'antd/lib/typography/Text';
import { isNil, last, max, memoize, min } from 'lodash';
import * as React from 'react';
import { displayStatValue, formatBytes, timeStringFromDuration } from '../helpers';
import {
    IntegrationType,
    StatChangeStatus,
    StatisticAdditionalData,
    StatisticScope,
    StatisticType,
    StatisticUnit,
    StatisticWithAssociatedInfo,
} from '../types';
import IntegrationIcon from './icons/IntegrationIcon';
import { ChangeValue } from './releaseOverview/TrendAndChangeValue';
import { antGrey, hexStringToRGBAString, runwayColors } from './styles/globalStyles';

interface StatCardProps {
    statisticLabel: string;
    statisticLabelTooltip?: string;
    statisticValue: string | React.ReactNode;
    changeStatisticLabel: string | React.ReactNode;
    changeStatisticValue: string | React.ReactNode;
    backgroundColor?: string;
    isLoading?: boolean;
    children?: React.ReactNode;
    integrationId?: IntegrationType;
}

const BASE_GRAPH_CONFIG: Partial<AreaConfig> = {
    xField: 'x',
    yField: 'y',
    animation: null,
    padding: [20, 0, 0, 0],
    isStack: false,
    legend: false,
    startOnZero: false,
    yAxis: {
        label: null,
        grid: null,
        tickLine: null,
        range: [0, 0.8],
        nice: true,
    },
    tooltip: {
        showMarkers: true,
        showTitle: true,
        domStyles: {
            'g2-tooltip': {
                backgroundColor: antGrey.grey8,
                color: 'white',
            },
        },
        crosshairs: {
            line: {
                style: {
                    stroke: hexStringToRGBAString(runwayColors.primary, 0.8),
                    lineDash: [2, 2],
                    lineWidth: 2,
                },
            },
        },
    },
    xAxis: {
        range: [0, 0.9],
        label: null,
        nice: true,
        line: null,
        grid: {
            line: {
                style: {
                    stroke: antGrey.grey4,
                    lineWidth: 2,
                },
            },
        },
    },
    annotations: [],
    line: {
        style: {
            lineWidth: 4,
            lineCap: 'butt',
            lineJoin: 'miter',
        },
    },
    areaStyle: {
        fillOpacity: 0.2,
    },
};

const StatCard: React.FC<StatCardProps> = (props: StatCardProps) => {
    const {
        statisticLabel,
        statisticValue,
        changeStatisticLabel,
        changeStatisticValue,
        backgroundColor,
        isLoading,
        statisticLabelTooltip,
        children,
        integrationId,
    } = props;

    return (
        <div
            style={{
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: backgroundColor ?? antGrey.grey2,
                color: antGrey.grey7,
                padding: '18px 18px 150px 18px',
            }}
        >
            {isLoading ?
                <Skeleton active />
            :   <>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Text style={{ color: antGrey.grey7 }}>{statisticLabel}</Text>
                            {statisticLabelTooltip && (
                                <Tooltip title={statisticLabelTooltip}>
                                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                </Tooltip>
                            )}
                            {integrationId && (
                                <div style={{ display: 'flex', marginLeft: 'auto' }}>
                                    <IntegrationIcon integrationId={integrationId} />
                                </div>
                            )}
                        </div>

                        <Text style={{ color: antGrey.grey8, fontSize: '24px' }}>{statisticValue ?? '--'}</Text>
                    </div>
                    {changeStatisticValue && changeStatisticLabel && (
                        <>
                            <Divider dashed style={{ margin: '12px 0px' }} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {changeStatisticLabel}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                    {changeStatisticValue}
                                </div>
                            </div>
                        </>
                    )}
                    {children && (
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                height: 140,
                                width: '100%',
                            }}
                        >
                            {children}
                        </div>
                    )}
                </>
            }
        </div>
    );
};

export interface StatData {
    x: number | string;
    y: number;
    releaseVersion: string;
    appName: string;
    appNameAndType: string;
    type: StatisticType;
}

function mapStatToData(stats: StatisticWithAssociatedInfo[], sortByTimestamp?: boolean): StatData[] {
    return stats.map((sampleStat) => ({
        x: sortByTimestamp ? sampleStat.completedAtOrReleasedAtTimestamp * 1000 : sampleStat.releaseVersion,
        y: sampleStat.value,
        releaseVersion: sampleStat.releaseVersion,
        appName: sampleStat.appName ?? '',
        type: sampleStat.type,
        appNameAndType: `${sampleStat.appName ?? ''} ${sampleStat.type}`,
    }));
}

const getGraphConfig: (
    isMultilineGraph,
    type: StatisticType,
    unit: StatisticUnit,
    sampleStats?: StatisticWithAssociatedInfo[],
    highlightedVersion?: string,
    secondarySampleStats?: StatisticWithAssociatedInfo[],
) => AreaConfig = (isMultilineGraph, type, unit, sampleStats, highlightedVersion, secondarySampleStats) => {
    if (isNil(sampleStats) || sampleStats.length < 1) {
        return null;
    }

    const allStats =
        secondarySampleStats && secondarySampleStats.length > 1 ?
            sampleStats.concat(secondarySampleStats)
        :   sampleStats;

    const simplifiedData: StatData[] = [];
    const sampleValues = allStats?.map((sampleStat) => sampleStat.value) ?? [];

    if (sampleValues.length <= 1) {
        return null;
    }

    if (isMultilineGraph) {
        allStats.sort((a, b) => a.completedAtOrReleasedAtTimestamp - b.completedAtOrReleasedAtTimestamp);

        const sampleStatsBySeriesField = allStats?.reduce(
            (acc, sampleStat) => {
                const seriesField =
                    secondarySampleStats ? `${sampleStat.appName ?? ''} ${sampleStat.type}` : sampleStat.appName;
                if (!acc[seriesField]) {
                    acc[seriesField] = [];
                }

                acc[seriesField].push({
                    ...sampleStat,
                });

                return acc;
            },
            {} as Record<string, StatisticWithAssociatedInfo[]>,
        );

        Object.entries(sampleStatsBySeriesField).forEach(([, stats]) => {
            if (stats.length > 1) {
                const everythingElse = mapStatToData(stats, true);
                simplifiedData.push(...everythingElse);
            }
        });

        if (simplifiedData.length <= 1) {
            return null;
        }
    }

    const minValue = min(sampleValues);
    const maxValue = max(sampleValues);

    let gapFromBottom = Math.abs(maxValue - minValue) * 0.1;

    if (gapFromBottom === 0 && minValue !== 0) {
        gapFromBottom = Math.abs(minValue) * 0.5;
    }

    const minYAxis = minValue - gapFromBottom;
    const lastStat = last(sampleStats);

    const highlightedStatIndex = sampleStats?.findIndex((stat) => stat.releaseVersion === highlightedVersion);

    const config =
        isMultilineGraph ?
            ({
                ...BASE_GRAPH_CONFIG,
                seriesField: secondarySampleStats ? 'appNameAndType' : 'appName',
                data: simplifiedData,
                line: {
                    ...BASE_GRAPH_CONFIG.line,
                    style: {
                        ...BASE_GRAPH_CONFIG.line.style,
                        strokeOpacity: 0.5,
                    },
                },
                meta: {
                    x: {
                        type: 'time',
                        format: 'dddd MMMM Do, YYYY',
                    },
                    y: {
                        type: 'linear',
                        formatter: (value) => displayStatValue(type, value, unit),
                    },
                },
                point: {
                    shape: 'circle',
                    size: 2,
                    style: {
                        strokeOpacity: 0,
                    },
                },
                xAxis: {
                    ...BASE_GRAPH_CONFIG.xAxis,
                    tickMethod: 'time',
                    mask: 'dddd MMMM Do, YYYY',
                    nice: false,
                    tickInterval: 24 * 3600 * 1000 * 7, // 7 days
                },
                yAxis: {
                    ...BASE_GRAPH_CONFIG.yAxis,
                    min: minYAxis,
                },
                areaStyle: {
                    fillOpacity: 0.08,
                },
                tooltip: {
                    ...BASE_GRAPH_CONFIG.tooltip,
                    shared: false,
                    fields: ['x', 'y', 'appName', 'releaseVersion', 'type'],
                    formatter: (datum: Datum) => {
                        return {
                            name:
                                secondarySampleStats ?
                                    `${datum.appName} ${datum.releaseVersion}`
                                :   `${datum.appName} ${datum.releaseVersion}`,
                            value: displayStatValue(type, datum.y, unit),
                        };
                    },
                },
            } as AreaConfig)
        :   ({
                ...BASE_GRAPH_CONFIG,
                data: mapStatToData(allStats),
                color: ['rgba(180, 189, 231, 1)', 'rgba(189, 180, 231, 1)'],
                seriesField: 'type',
                xAxis: {
                    ...BASE_GRAPH_CONFIG.xAxis,
                },
                yAxis: {
                    ...BASE_GRAPH_CONFIG.yAxis,
                    min: minYAxis,
                },
                line: {
                    ...BASE_GRAPH_CONFIG.line,
                },
                areaStyle: {
                    fillOpacity: 0.5,
                },
                annotations: [
                    ...BASE_GRAPH_CONFIG.annotations,
                    ...(highlightedVersion && highlightedStatIndex !== -1 ?
                        [
                            {
                                type: 'line',
                                start: [highlightedStatIndex, 'start'],
                                end: [highlightedStatIndex, 'end'],
                                style: {
                                    lineWidth: 2,
                                    stroke: runwayColors.primary,
                                },
                                animate: false,
                            },
                            {
                                type: 'text',
                                position: [highlightedStatIndex, 'end'],
                                content: highlightedVersion,
                                offsetY: -8,
                                color: antGrey.grey8,
                                style: {
                                    textBaseline: 'bottom',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    fontSize: 12,
                                },
                            },
                        ]
                    :   [
                            {
                                type: 'line',
                                start: ['max', 'start'],
                                end: ['max', 'end'],
                                style: {
                                    lineWidth: 2,
                                    stroke: runwayColors.primary,
                                },
                                animate: false,
                            },
                            {
                                type: 'text',
                                position: ['max', 'end'],
                                content: lastStat?.releaseVersion ?? '',
                                offsetY: -8,
                                color: antGrey.grey8,
                                style: {
                                    textBaseline: 'bottom',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    fontSize: 12,
                                },
                            },
                        ]),
                ],
                tooltip: {
                    ...BASE_GRAPH_CONFIG.tooltip,
                    showTitle: !!secondarySampleStats,
                    title: (title, datum) => {
                        return datum.releaseVersion;
                    },
                    fields: ['x', 'y', 'appName', 'releaseVersion', 'type'],
                    formatter: (datum: Datum) => {
                        return {
                            name: datum.releaseVersion,
                            value: displayStatValue(type, datum.y, unit),
                        };
                    },
                },
            } as AreaConfig);

    return config;
};

const memoizedGetGraphConfig = memoize(
    getGraphConfig,
    (isMultilineGraph, type, unit, sampleStats, highlightedVersion) => {
        return `${isMultilineGraph}-${type}-${unit}-${sampleStats
            ?.map((sampleStat) => sampleStat.value)
            .join(',')}-${highlightedVersion}`;
    },
);

const statisticTooltipForScope = (scope: StatisticScope, statisticType?: StatisticType): string | null => {
    switch (scope) {
        case StatisticScope.App:
            if (statisticType === StatisticType.HotfixReleaseDuration) {
                return `considering the last ${RELEASE_LOOKBACK} hotfix releases`;
            }

            return `considering the last ${RELEASE_LOOKBACK} releases (excluding hotfix releases)`;
        case StatisticScope.AppGroup:
            if (
                statisticType === StatisticType.InReviewDuration ||
                statisticType === StatisticType.WaitingForReviewDuration
            ) {
                return `considering the last ${RELEASE_LOOKBACK} releases (excluding hotfix releases) of each iOS app`;
            }
            if (statisticType === StatisticType.HotfixReleaseDuration) {
                return `considering the last ${RELEASE_LOOKBACK} hotfix releases of each app`;
            }

            return `considering the last ${RELEASE_LOOKBACK} releases (excluding hotfix releases) of each app`;
        case StatisticScope.Org:
            if (
                statisticType === StatisticType.InReviewDuration ||
                statisticType === StatisticType.WaitingForReviewDuration
            ) {
                return `considering the last ${RELEASE_LOOKBACK} releases (excluding hotfix releases) of each app, for every iOS app in the organization`;
            }
            if (statisticType === StatisticType.HotfixReleaseDuration) {
                return `considering the last ${RELEASE_LOOKBACK} hotfix releases of each app, for every app in the organization`;
            }

            return `considering the last ${RELEASE_LOOKBACK} releases (excluding hotfix releases) of each app, for every app in the organization`;
        default:
            return null;
    }
};

export const MIN_REQUIRED_AUTOMATION_COUNT = 500;

interface AutomationsStatCardProps {
    actions: number;
    timeSaved: number;
    costSaved: number;
    scope?: StatisticScope;
    type: StatisticType;
    sampleStats?: StatisticWithAssociatedInfo[];
}

export const AutomationsStatCard: React.FC<AutomationsStatCardProps> = (props: AutomationsStatCardProps) => {
    const { actions, timeSaved, scope, type } = props;
    const isAverageStat = type === StatisticType.StatAverageAutomationsCount;

    let statisticLabelTooltip = null;
    switch (scope) {
        case StatisticScope.App:
            statisticLabelTooltip =
                isAverageStat ? `Average ${statisticTooltipForScope(scope, type)}` : 'Across all releases';
            break;
        case StatisticScope.AppGroup:
            statisticLabelTooltip =
                isAverageStat ? `Average ${statisticTooltipForScope(scope, type)}` : 'Across all platforms of the app';
            break;
        case StatisticScope.Org:
            statisticLabelTooltip =
                isAverageStat ?
                    `Average ${statisticTooltipForScope(scope, type)}`
                :   'Across all apps in the organization';
            break;
        case StatisticScope.Release:
            statisticLabelTooltip = 'For this release';
            break;
        default:
            break;
    }

    const statisticLabel = isAverageStat ? 'Avg # of Runway automations' : 'Runway automations';
    const changeStatisticLabel = `${isAverageStat ? `Avg t` : `T`}ime saved`;

    return (
        <StatCard
            backgroundColor="#6A7FDB1A"
            statisticLabelTooltip={statisticLabelTooltip}
            statisticLabel={statisticLabel}
            statisticValue={
                <>
                    <ThunderboltFilled style={{ fontSize: 18, color: runwayColors.primary, marginRight: 7 }} />
                    {displayStatValue(StatisticType.StatTotalAutomationsCount, actions, StatisticUnit.Count)}
                </>
            }
            changeStatisticLabel={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Text style={{ color: antGrey.grey7 }}>{changeStatisticLabel}</Text>
                    {statisticLabelTooltip && (
                        <Tooltip title={statisticLabelTooltip}>
                            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                        </Tooltip>
                    )}
                </div>
            }
            changeStatisticValue={
                timeSaved && (
                    <>
                        <ClockCircleOutlined style={{ fontSize: 18, color: runwayColors.primary }} />
                        <Text style={{ fontSize: 24, fontWeight: 200, color: antGrey.grey8 }}>
                            {displayStatValue(
                                StatisticType.StatTotalTimeSavedFromAutomations,
                                timeSaved,
                                StatisticUnit.Seconds,
                            )}
                        </Text>
                    </>
                )
            }
        />
    );
};

interface ReleaseOverviewStatCardProps {
    type: StatisticType;
    value: number | React.ReactNode;
    change: number | React.ReactNode;
    changeStatus?: StatChangeStatus | null;
    unit?: StatisticUnit;
    average?: number | string;
    isDeveloperRejected?: boolean;
    isStoreRejected?: boolean;
    wasRejected?: boolean;
    isLoadingStats?: boolean;
    highlightedVersion?: string;
    sampleStats?: StatisticWithAssociatedInfo[];
    secondarySampleStats?: StatisticWithAssociatedInfo[];
    integrationId?: IntegrationType;
    additionalData?: StatisticAdditionalData;
}

export const ReleaseOverviewStatCard: React.FC<ReleaseOverviewStatCardProps> = (
    props: ReleaseOverviewStatCardProps,
) => {
    const {
        type,
        value,
        change,
        changeStatus,
        unit,
        average,
        sampleStats,
        secondarySampleStats,
        isLoadingStats,
        highlightedVersion,
        isDeveloperRejected = false,
        isStoreRejected = false,
        wasRejected = false,
        integrationId,
        additionalData,
    } = props;

    let statisticLabelTooltip = null;
    if (isDeveloperRejected || isStoreRejected || wasRejected) {
        const rejectionTypeString =
            isDeveloperRejected || isStoreRejected ?
                `This release was  ${isDeveloperRejected ? 'developer' : ''} rejected.`
            :   '';
        const baseTooltipString = wasRejected ? 'Duration is for the latest app store submission.' : '';
        statisticLabelTooltip = `${rejectionTypeString} ${baseTooltipString}`;
    }

    if (additionalData?.eventName !== '') {
        switch (type) {
            case StatisticType.AverageEventsPerSession:
                statisticLabelTooltip = 'Average number of events per session';
                break;
            case StatisticType.AverageEventsPerDailyActiveUser:
                statisticLabelTooltip = 'Average number of events per daily active user';
                break;
            case StatisticType.PercentWorkItemsInFixRequests:
                statisticLabelTooltip = 'The percentage of work items included in the release that were fixes.';
                break;
        }
    }

    const graphConfig = memoizedGetGraphConfig(
        false,
        type,
        unit,
        sampleStats,
        highlightedVersion,
        secondarySampleStats,
    );

    return (
        <StatCard
            statisticLabel={displayReleaseOverviewStatisticLabel(type, additionalData)}
            statisticLabelTooltip={statisticLabelTooltip}
            statisticValue={typeof value === 'number' ? displayStatValue(type, value, unit) : value}
            changeStatisticLabel={displayReleaseOverviewStatisticChangeLabel(type, average)}
            changeStatisticValue={
                typeof change === 'number' ?
                    <ChangeValue change={change} changeStatus={changeStatus} unit={unit} type={type} />
                :   change
            }
            integrationId={integrationId}
            isLoading={isLoadingStats}
        >
            {graphConfig && <Area {...graphConfig} />}
        </StatCard>
    );
};

export const displayReleaseOverviewStatisticLabel: (
    statisticType: StatisticType,
    additionalData?: StatisticAdditionalData,
) => string = (statisticType, additionalData) => {
    switch (statisticType) {
        case StatisticType.AppStoreBuildCount:
            return 'App store builds';
        case StatisticType.CIAverageBuildTime:
            return 'Average build time';
        case StatisticType.CIWorkflowSuccessPercent:
            return 'Build workflow success rate';
        case StatisticType.CommitCount:
            return 'Number of commits';
        case StatisticType.ItemsCompleted:
            return 'Items of work completed';
        case StatisticType.FixesCount:
            return 'Fixes';
        case StatisticType.FixRequestsCount:
            return 'Fix requests';
        case StatisticType.FixRequestsApproved:
            return 'Fix requests approved';
        case StatisticType.FixRequestsRejected:
            return 'Fix requests rejected';
        case StatisticType.PercentWorkItemsInFixRequests:
            return 'Completed work items fix percentage';
        case StatisticType.FilesChangedCount:
            return 'Files changed';
        case StatisticType.InReviewDuration:
            return 'In review';
        case StatisticType.LinesOfCodeChanged:
            return 'Lines of code';
        case StatisticType.AppStoreBuildBundleDownloadSize:
            return 'Bundle download size';
        case StatisticType.AppStoreBuildBundleInstallSize:
            return 'Bundle install size';
        case StatisticType.ReleaseDuration:
            return 'Release duration';
        case StatisticType.WaitingForReviewDuration:
            return 'Waiting for review';
        case StatisticType.CrashFreeUsers:
            return 'Crash-free users';
        case StatisticType.CrashFreeSessions:
            return 'Crash-free sessions';
        case StatisticType.StatAverageCustomerRating:
            return 'Average user rating';
        case StatisticType.AverageEventsPerSession:
        case StatisticType.AverageEventsPerDailyActiveUser:
            return additionalData?.eventName ?? '';
        default:
            return '';
    }
};

const RELEASE_LOOKBACK = 5;
const displayReleaseOverviewStatisticChangeLabel: (
    statisticType: StatisticType,
    average: number | string,
) => React.ReactNode = (statisticType, average) => {
    let changeText = '';
    let tooltipText = '';
    switch (statisticType) {
        case StatisticType.InReviewDuration:
            changeText = 'Relative to average review time';
            tooltipText = `Average review time experienced by all teams using Runway: ${timeStringFromDuration(
                average as number,
            )}.`;
            break;
        case StatisticType.WaitingForReviewDuration:
            changeText = 'Relative to average wait time';
            tooltipText = `Average wait time experienced by all teams using Runway: ${timeStringFromDuration(
                average as number,
            )}.`;
            break;
        case StatisticType.ReleaseDuration:
        case StatisticType.CIAverageBuildTime:
            changeText = 'Change';
            tooltipText = `Compared to average (${timeStringFromDuration(
                average as number,
            )}) across the last ${RELEASE_LOOKBACK} releases for this app.`;
            break;
        case StatisticType.CrashFreeSessions:
        case StatisticType.CrashFreeUsers: {
            const formattedAverage = average ? `(${(average as number).toFixed(3)}%) ` : '';
            changeText = 'Change';
            tooltipText = `Compared to average ${formattedAverage}across the last ${RELEASE_LOOKBACK} releases for this app.`;
            break;
        }
        case StatisticType.CIWorkflowSuccessPercent: {
            changeText = 'Change';
            tooltipText = `Compared to average (${average}%) across the last ${RELEASE_LOOKBACK} releases for this app.`;
            break;
        }
        case StatisticType.StatAverageCustomerRating: {
            const formattedAverage = average ? `(${(average as number).toFixed(2)}) ` : '';
            changeText = 'Change';
            tooltipText = `Compared to average ${formattedAverage}across the last ${RELEASE_LOOKBACK} releases for this app.`;
            break;
        }
        case StatisticType.AppStoreBuildBundleInstallSize:
        case StatisticType.AppStoreBuildBundleDownloadSize:
            changeText = 'Change';
            tooltipText = `Compared to the last release (${formatBytes(average as number, 2)}).`;
            break;
        case StatisticType.AverageEventsPerDailyActiveUser:
        case StatisticType.AverageEventsPerSession:
            changeText = 'Change';
            tooltipText = `Compared to average (${average}) across the last ${RELEASE_LOOKBACK} releases.`;
            break;
        default: {
            changeText = 'Change';
            tooltipText = `Compared to average (${average}) across the last ${RELEASE_LOOKBACK} releases.`;
            break;
        }
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Text style={{ color: antGrey.grey7 }}>{changeText}</Text>
            <Tooltip title={tooltipText}>
                <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
            </Tooltip>
        </div>
    );
};
